import { useEffect, useRef, useState } from "react";
import { Bot, Loader2, RotateCcw, Send } from "lucide-react";
import { ScreenHeader } from "@/components/AppUI";
import { PIZZERIA_URL, getToken, papi } from "@/lib/pizzeria";

const SID_KEY = "p400_chat_sid";
const WELCOME = { role: "assistant", text: "Ciao! Sono l'assistente della Pizzeria 400° Gradi. Chiedimi del menù, degli orari o di come ordinare." };

const Bubble = ({ m, i }) => {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${i}`}>
      <p className="max-w-[85%] rounded-2xl px-4 py-2.5 text-[15px] leading-[21px] whitespace-pre-line"
        style={mine ? { background: "var(--brand)", color: "var(--on-brand)", borderBottomRightRadius: 6 } : { background: "var(--surface-2)", border: "1px solid var(--border-c)", borderBottomLeftRadius: 6 }}>
        {m.text || <Loader2 className="h-4 w-4 animate-spin" />}
      </p>
    </div>
  );
};

const streamChat = async (body, onEvent) => {
  const headers = { "Content-Type": "application/json" };
  const t = getToken();
  if (t) headers.Authorization = `Bearer ${t}`;
  const res = await fetch(`${PIZZERIA_URL}/api/assistant/chat`, { method: "POST", headers, body: JSON.stringify(body) });
  if (!res.ok || !res.body) throw new Error("chat");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => {
      const line = p.split("\n").find((l) => l.startsWith("data:"));
      if (line) {
        try {
          onEvent(JSON.parse(line.slice(5).trim()));
        } catch {
          /* ignore partial */
        }
      }
    });
  }
};

export default function AssistantPage() {
  const [sid, setSid] = useState(() => localStorage.getItem(SID_KEY));
  const [msgs, setMsgs] = useState([WELCOME]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => {
    if (sid) {
      papi.get(`/assistant/chats/${sid}`).then(({ data }) => {
        const list = (Array.isArray(data) ? data : data.messages || []).map((m) => ({ role: m.role, text: m.text ?? m.content ?? "" }));
        if (list.length) setMsgs([WELCOME, ...list]);
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = async () => {
    const msg = text.trim();
    if (!msg || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    const append = (t) => setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, text: x.text + t } : x)));
    try {
      await streamChat({ message: msg, ...(sid && { session_id: sid }) }, (ev) => {
        if (ev.type === "session" && ev.id) {
          localStorage.setItem(SID_KEY, ev.id);
          setSid(ev.id);
        } else if (ev.type === "delta") append(ev.text || "");
        else if (ev.type === "error") append(ev.message || "Scusa, c'è stato un problema. Riprova.");
      });
    } catch {
      append("Scusa, non riesco a rispondere adesso. Riprova tra poco.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    if (sid) papi.delete(`/assistant/chats/${sid}`).catch(() => {});
    localStorage.removeItem(SID_KEY);
    setSid(null);
    setMsgs([WELCOME]);
  };

  return (
    <div className="flex-1 flex flex-col h-screen" data-testid="assistant-page">
      <ScreenHeader title="Assistente 400°" />
      <div className="flex items-center gap-2 px-4 py-2 text-xs" style={{ color: "var(--muted-c)" }}>
        <Bot className="h-4 w-4" /><span className="flex-1">Risposte automatiche: per ordini e modifiche chiama la pizzeria.</span>
        <button onClick={reset} className="flex items-center gap-1 font-medium" style={{ color: "var(--brand)" }} data-testid="chat-reset-button"><RotateCcw className="h-3.5 w-3.5" />Nuova chat</button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3" data-testid="chat-messages">
        {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 p-3" style={{ borderTop: "1px solid var(--border-c)", background: "var(--surface)" }}>
        <input value={text} onChange={(e) => setText(e.target.value.slice(0, 500))} placeholder="Scrivi un messaggio..." data-testid="chat-input"
          className="flex-1 h-12 rounded-full px-4 outline-none" style={{ background: "var(--surface-2)", border: "1px solid var(--border-c)", color: "var(--on-surface)" }} />
        <button type="submit" disabled={busy || !text.trim()} className="h-12 w-12 rounded-full grid place-items-center disabled:opacity-40" style={{ background: "var(--brand)", color: "var(--on-brand)" }} data-testid="chat-send-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </button>
      </form>
    </div>
  );
}
